import {
  createMapBackup,
  prepareBackupCopy,
  type BackupHash,
  type VerifiedBackup,
  createLibraryBackup,
  prepareLibraryCopy,
  type VerifiedLibraryBackup,
} from '@orbit-mind/api-client';
import { parseLibrary, type MapDocument } from '@orbit-mind/domain';
import {
  stageAttachmentRestore,
  writeStagedAttachment,
} from '@orbit-mind/offline-engine/indexeddb';
import { randomIds, readLibraryDocument } from '@orbit-mind/map-engine';
import { unwrap, type MapId, type Timestamp } from '@orbit-mind/shared-types';
import { LOCAL_AUTHOR, LOCAL_WORKSPACE } from './useOpenMap';
import type { LocalStorageLayer } from './storage';

export const backupHash: BackupHash = async (bytes) =>
  [...new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(bytes)))]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('') as Awaited<ReturnType<BackupHash>>;
export async function downloadMapBackup(layer: LocalStorageLayer, mapId: MapId): Promise<void> {
  const document = await readLibraryDocument(layer.journal, mapId);
  const text = await createMapBackup({
    document,
    hash: backupHash,
    now: Date.now(),
    readFile: async (id) => {
      const attachment = unwrap(await layer.attachments.get(id));
      if (attachment === undefined) return undefined;
      const blob = unwrap(await layer.attachments.bytesOf(attachment.storageKey));
      return blob === undefined
        ? undefined
        : { attachment, bytes: new Uint8Array(await blob.arrayBuffer()) };
    },
  });
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = 'orbit-mind.orbitbackup';
  anchor.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
export async function restoreBackup(
  layer: LocalStorageLayer,
  backup: VerifiedBackup,
): Promise<MapId> {
  const now = Date.now() as Timestamp;
  const copy = prepareBackupCopy(backup, {
    mapId: `map_${randomIds().nodeId()}` as MapId,
    ownerId: LOCAL_AUTHOR,
    workspaceId: LOCAL_WORKSPACE,
    now,
  });
  const staged: Awaited<ReturnType<typeof stageAttachmentRestore>>[] = [];
  for (const file of copy.files)
    staged.push(await stageAttachmentRestore(file.attachment, file.bytes));
  await layer.db.transaction('rw', layer.db.tables, async () => {
    for (const file of staged) await writeStagedAttachment(layer.db, file);
    unwrap(await layer.catalog.create(copy.document, now));
  });
  return copy.document.map.id;
}

export async function downloadLibraryBackup(layer: LocalStorageLayer): Promise<void> {
  const listing = unwrap(await layer.catalog.list({ includeTrashed: true }));
  if (listing.damaged.length > 0)
    throw new Error('Recupere os mapas danificados antes de exportar a biblioteca.');
  const documents: MapDocument[] = [];
  for (const item of listing.maps)
    documents.push(await readLibraryDocument(layer.journal, item.map.id));
  const organization = parseLibrary(
    JSON.parse(
      (await layer.db.settings.get('library-organization'))?.value ??
        '{"folders":[],"placements":{}}',
    ),
  );
  const text = await createLibraryBackup({
    documents,
    organization: {
      ...organization,
      placements: Object.fromEntries(
        Object.entries(organization.placements).filter(([id]) =>
          documents.some((doc) => doc.map.id === id),
        ),
      ),
    },
    hash: backupHash,
    now: Date.now(),
    readFile: async (id) => {
      const attachment = unwrap(await layer.attachments.get(id));
      if (!attachment) return undefined;
      const blob = unwrap(await layer.attachments.bytesOf(attachment.storageKey));
      return blob ? { attachment, bytes: new Uint8Array(await blob.arrayBuffer()) } : undefined;
    },
  });
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = 'orbit-mind-library.orbitbackup';
  anchor.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

export async function restoreLibraryBackup(
  layer: LocalStorageLayer,
  backup: VerifiedLibraryBackup,
): Promise<void> {
  const now = Date.now() as Timestamp;
  const copy = prepareLibraryCopy(backup, {
    newMapId: () => `map_${randomIds().nodeId()}` as MapId,
    ownerId: LOCAL_AUTHOR,
    workspaceId: LOCAL_WORKSPACE,
    now,
    prefix: `restore_${randomIds().nodeId()}`,
  });
  const staged: Awaited<ReturnType<typeof stageAttachmentRestore>>[] = [];
  for (const map of copy.maps)
    for (const file of map.files)
      staged.push(await stageAttachmentRestore(file.attachment, file.bytes));
  await layer.db.transaction('rw', layer.db.tables, async () => {
    for (const file of staged) await writeStagedAttachment(layer.db, file);
    for (const map of copy.maps) {
      unwrap(await layer.catalog.create(map.document, now));
    }
    const previous = parseLibrary(
      JSON.parse(
        (await layer.db.settings.get('library-organization'))?.value ??
          '{"folders":[],"placements":{}}',
      ),
    );
    await layer.db.settings.put({
      key: 'library-organization',
      value: JSON.stringify({
        folders: [...previous.folders, ...copy.organization.folders],
        placements: { ...previous.placements, ...copy.organization.placements },
      }),
    });
  });
}
